import React, { useEffect, useMemo, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  Divider,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  Link,
  MenuItem,
  Paper,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import {
  DeleteOutline as DeleteIcon,
  Refresh as RefreshIcon,
} from '@mui/icons-material';
import { Link as RouterLink } from 'react-router-dom';
import { PageLayout } from '../layout/primitives';
import { useTasksStore, type Task, type TaskCreatePayload, type TaskSection, type TaskStatus } from '../store/tasks';
import { useBooksStore } from '../store/books';
import { useNotesStore } from '../store/notes';

type KanbanColumn = TaskStatus;

interface GeneratedDailyCard {
  id: string;
  title: string;
  description: string;
  metadataLabel?: string;
  href: string;
  ctaLabel: string;
}

const COLUMN_META: Array<{ key: KanbanColumn; label: string }> = [
  { key: 'todo', label: 'To do' },
  { key: 'in_progress', label: 'In progress' },
  { key: 'done', label: 'Done' },
];

const formatDueDate = (value: string | null): string | null => {
  if (!value) {
    return null;
  }
  return new Date(`${value}T00:00:00`).toLocaleDateString([], {
    weekday: 'short',
    month: 'short',
    day: 'numeric',
  });
};

const TaskCard: React.FC<{
  task: Task;
  onStatusChange: (taskId: string, status: TaskStatus) => void;
  onDelete: (task: Task) => void;
}> = ({ task, onStatusChange, onDelete }) => {
  const dueDate = formatDueDate(task.due_date);

  return (
    <Card variant="outlined" sx={{ borderRadius: 3 }}>
      <CardContent sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
        <Box sx={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 1 }}>
          <Box sx={{ minWidth: 0 }}>
            <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
              {task.title}
            </Typography>
          </Box>
          <Stack direction="row" spacing={0.5} alignItems="center" sx={{ flexShrink: 0 }}>
            <Chip
              size="small"
              color={task.section === 'project' ? 'primary' : 'default'}
              label={task.section === 'project' ? 'Project' : 'Daily'}
            />
            <IconButton
              size="small"
              aria-label={`Delete ${task.title}`}
              color="error"
              onClick={() => onDelete(task)}
            >
              <DeleteIcon fontSize="small" />
            </IconButton>
          </Stack>
        </Box>
        <Typography variant="body2" color="text.secondary">
          {task.description}
        </Typography>
        <Stack direction="row" spacing={1} useFlexGap flexWrap="wrap">
          {dueDate ? <Chip size="small" variant="outlined" label={`Due ${dueDate}`} /> : null}
          <Chip size="small" variant="outlined" label={task.note_title} />
        </Stack>
        <Stack direction="row" spacing={1} useFlexGap flexWrap="wrap" alignItems="center">
          <TextField
            select
            size="small"
            label="Status"
            value={task.status}
            onChange={(event) => onStatusChange(task.id, event.target.value as TaskStatus)}
            sx={{ minWidth: 160 }}
          >
            {COLUMN_META.map((column) => (
              <MenuItem key={column.key} value={column.key}>
                {column.label}
              </MenuItem>
            ))}
          </TextField>
          <Button component={RouterLink} to={`/notes?open=${task.note_id}`} size="small">
            Note
          </Button>
          {task.calendar_html_link ? (
            <Button
              component="a"
              href={task.calendar_html_link}
              target="_blank"
              rel="noreferrer"
              size="small"
            >
              Calendar
            </Button>
          ) : null}
        </Stack>
      </CardContent>
    </Card>
  );
};

const GeneratedCard: React.FC<{ card: GeneratedDailyCard }> = ({ card }) => (
  <Card variant="outlined" sx={{ borderRadius: 3, bgcolor: 'action.hover' }}>
    <CardContent sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
      <Box sx={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 1 }}>
        <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
          {card.title}
        </Typography>
        <Chip size="small" color="secondary" label="Generated" />
      </Box>
      <Typography variant="body2" color="text.secondary">
        {card.description}
      </Typography>
      <Stack direction="row" spacing={1} useFlexGap flexWrap="wrap">
        {card.metadataLabel ? <Chip size="small" variant="outlined" label={card.metadataLabel} /> : null}
        <Link component={RouterLink} to={card.href} underline="hover" sx={{ alignSelf: 'center' }}>
          {card.ctaLabel}
        </Link>
      </Stack>
    </CardContent>
  </Card>
);

const KanbanColumnView: React.FC<{
  title: string;
  tasks: Task[];
  generatedCards?: GeneratedDailyCard[];
  onStatusChange: (taskId: string, status: TaskStatus) => void;
  onDeleteTask: (task: Task) => void;
}> = ({ title, tasks, generatedCards = [], onStatusChange, onDeleteTask }) => (
  <Paper
    variant="outlined"
    sx={{
      p: 2,
      borderRadius: 4,
      display: 'flex',
      flexDirection: 'column',
      gap: 1.5,
      minHeight: 240,
      bgcolor: 'background.paper',
    }}
  >
    <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 1 }}>
      <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>
        {title}
      </Typography>
      <Chip size="small" label={tasks.length + generatedCards.length} />
    </Box>
    <Divider />
    <Stack spacing={1.5}>
      {generatedCards.map((card) => (
        <GeneratedCard key={card.id} card={card} />
      ))}
      {tasks.map((task) => (
        <TaskCard key={task.id} task={task} onStatusChange={onStatusChange} onDelete={onDeleteTask} />
      ))}
      {tasks.length === 0 && generatedCards.length === 0 ? (
        <Typography variant="body2" color="text.secondary">
          Nothing here yet.
        </Typography>
      ) : null}
    </Stack>
  </Paper>
);

const TasksPage: React.FC = () => {
  const { tasks, loading, saving, error, fetchTasks, createTask, updateTaskStatus, deleteTask, clearError } = useTasksStore();
  const { books, fetchBooks, error: booksError } = useBooksStore();
  const { notes, fetchNotes, error: notesError } = useNotesStore();
  const [form, setForm] = useState<TaskCreatePayload>({
    title: '',
    description: '',
    section: 'project',
    due_date: '',
  });
  const [deleteTarget, setDeleteTarget] = useState<Task | null>(null);
  const [deleting, setDeleting] = useState(false);

  useEffect(() => {
    void fetchTasks();
    void fetchBooks();
    void fetchNotes();
  }, [fetchBooks, fetchNotes, fetchTasks]);

  const projectTasks = useMemo(() => tasks.filter((task) => task.section === 'project'), [tasks]);
  const dailyTasks = useMemo(() => tasks.filter((task) => task.section === 'daily'), [tasks]);

  const generatedDailyCards = useMemo<GeneratedDailyCard[]>(() => {
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    return books
      .filter((book) => book.status === 'reading' && book.total_positions > book.current_position)
      .map((book) => {
        const dueDate = new Date(`${book.due_date}T00:00:00`);
        const millisecondsPerDay = 24 * 60 * 60 * 1000;
        const rawDaysRemaining = Math.floor((dueDate.getTime() - today.getTime()) / millisecondsPerDay) + 1;
        const daysRemaining = Math.max(rawDaysRemaining, 1);
        const remainingPages = Math.max(book.total_positions - book.current_position, 0);
        const dailyPages = Math.max(Math.ceil(remainingPages / daysRemaining), 1);
        const dueLabel = formatDueDate(book.due_date);
        const paceSummary =
          rawDaysRemaining < 1
            ? `The ${dueLabel} target has passed, so ${dailyPages} pages today starts catching up.`
            : rawDaysRemaining === 1
              ? `${dailyPages} pages today finishes this on time.`
              : `Reading ${dailyPages} pages today keeps you on pace for ${dueLabel}.`;
        return {
          id: `reading-goal-${book.id}`,
          title: `Read ${dailyPages} pages of ${book.title}`,
          description: `You're at ${book.current_display}. ${paceSummary}`,
          metadataLabel: book.author,
          href: '/books',
          ctaLabel: 'Open Books',
        };
      });
  }, [books]);

  const tasksByColumn = (items: Task[]) =>
    COLUMN_META.reduce<Record<KanbanColumn, Task[]>>(
      (accumulator, column) => ({
        ...accumulator,
        [column.key]: items.filter((task) => task.status === column.key),
      }),
      { todo: [], in_progress: [], done: [] }
    );

  const projectColumns = useMemo(() => tasksByColumn(projectTasks), [projectTasks]);
  const dailyColumns = useMemo(() => tasksByColumn(dailyTasks), [dailyTasks]);

  const handleFieldChange = <K extends keyof TaskCreatePayload>(field: K, value: TaskCreatePayload[K]) => {
    setForm((current) => ({
      ...current,
      [field]: value,
      ...(field === 'section' && value === 'daily' ? { due_date: '' } : {}),
    }));
  };

  const handleCreateTask = async (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    clearError();
    const payload: TaskCreatePayload = {
      title: form.title.trim(),
      description: form.description.trim(),
      section: form.section,
      ...(form.note_id ? { note_id: form.note_id } : {}),
      ...(form.section === 'project' ? { due_date: form.due_date?.trim() || undefined } : {}),
    };
    const created = await createTask(payload);
    if (!created) {
      return;
    }
    if (!payload.note_id) {
      void fetchNotes();
    }
    setForm({ title: '', description: '', section: form.section, due_date: form.section === 'project' ? '' : undefined, note_id: '' });
  };

  const refreshAll = () => {
    clearError();
    void fetchTasks();
    void fetchBooks();
    void fetchNotes();
  };

  const handleDeleteTask = async () => {
    if (!deleteTarget) {
      return;
    }
    setDeleting(true);
    const deleted = await deleteTask(deleteTarget.id);
    setDeleting(false);
    if (deleted) {
      setDeleteTarget(null);
    }
  };

  return (
    <PageLayout contentMaxWidth={1800} paddingMode="shell">
      <Stack spacing={3}>
        <Box sx={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 2 }}>
          <Box>
            <Typography variant="h4" sx={{ mb: 1 }}>
              Tasks
            </Typography>
            <Typography color="text.secondary">
              Plan projects on a kanban board, keep daily tasks together, and auto-post project work onto your calendar.
            </Typography>
          </Box>
          <Button
            variant="outlined"
            startIcon={<RefreshIcon />}
            onClick={refreshAll}
            disabled={loading || saving || deleting}
          >
            Refresh
          </Button>
        </Box>

        {error ? <Alert severity="error">{error}</Alert> : null}
        {booksError ? <Alert severity="warning">Reading goals are unavailable: {booksError}</Alert> : null}
        {notesError ? <Alert severity="warning">Note picker is unavailable: {notesError}</Alert> : null}

        <Paper
          component="form"
          onSubmit={handleCreateTask}
          variant="outlined"
          sx={{ p: 3, borderRadius: 4 }}
        >
          <Stack spacing={2}>
            <Typography variant="h6">Create a task</Typography>
            <Stack direction={{ xs: 'column', md: 'row' }} spacing={2}>
              <TextField
                label="Title"
                value={form.title}
                onChange={(event) => handleFieldChange('title', event.target.value)}
                required
                fullWidth
              />
              <TextField
                select
                label="Section"
                value={form.section}
                onChange={(event) => handleFieldChange('section', event.target.value as TaskSection)}
                sx={{ minWidth: { md: 180 } }}
              >
                <MenuItem value="project">Projects</MenuItem>
                <MenuItem value="daily">Daily</MenuItem>
              </TextField>
              {form.section === 'project' ? (
                <TextField
                  label="Due date"
                  type="date"
                  value={form.due_date ?? ''}
                  onChange={(event) => handleFieldChange('due_date', event.target.value)}
                  InputLabelProps={{ shrink: true }}
                  required
                  sx={{ minWidth: { md: 180 } }}
                />
              ) : null}
            </Stack>
            <TextField
              label="Brief description"
              value={form.description}
              onChange={(event) => handleFieldChange('description', event.target.value)}
              required
              multiline
              minRows={3}
            />
            <TextField
              select
              label="Reference note"
              value={form.note_id ?? ''}
              onChange={(event) => handleFieldChange('note_id', event.target.value)}
              helperText="Leave blank to turn this description into a new note automatically."
            >
              <MenuItem value="">Create a new note from this description</MenuItem>
              {notes.map((note) => (
                <MenuItem key={note.id} value={note.id}>
                  {note.title}
                </MenuItem>
              ))}
            </TextField>
            <Box sx={{ display: 'flex', justifyContent: 'flex-end' }}>
              <Button type="submit" variant="contained" disabled={saving}>
                {saving ? 'Creating…' : 'Add task'}
              </Button>
            </Box>
          </Stack>
        </Paper>

        <Stack spacing={4}>
          <Box>
            <Typography variant="h5" sx={{ mb: 0.5 }}>
              Projects
            </Typography>
            <Typography color="text.secondary" sx={{ mb: 2 }}>
              Longer-running work that gets an all-day calendar event on its due date.
            </Typography>
            <Box
              sx={{
                display: 'grid',
                gridTemplateColumns: { xs: '1fr', lg: 'repeat(3, minmax(0, 1fr))' },
                gap: 2,
              }}
            >
              {COLUMN_META.map((column) => (
                <KanbanColumnView
                  key={`project-${column.key}`}
                  title={column.label}
                  tasks={projectColumns[column.key]}
                  onStatusChange={updateTaskStatus}
                  onDeleteTask={setDeleteTarget}
                />
              ))}
            </Box>
          </Box>

          <Box>
            <Typography variant="h5" sx={{ mb: 0.5 }}>
              Daily
            </Typography>
            <Typography color="text.secondary" sx={{ mb: 2 }}>
              Daily execution, including generated reading goals for every active book.
            </Typography>
            <Box
              sx={{
                display: 'grid',
                gridTemplateColumns: { xs: '1fr', lg: 'repeat(3, minmax(0, 1fr))' },
                gap: 2,
              }}
            >
              {COLUMN_META.map((column) => (
                <KanbanColumnView
                  key={`daily-${column.key}`}
                  title={column.label}
                  tasks={dailyColumns[column.key]}
                  generatedCards={column.key === 'todo' ? generatedDailyCards : []}
                  onStatusChange={updateTaskStatus}
                  onDeleteTask={setDeleteTarget}
                />
              ))}
            </Box>
          </Box>
        </Stack>

        {loading ? (
          <Typography color="text.secondary">Loading tasks…</Typography>
        ) : null}

        <Dialog
          open={Boolean(deleteTarget)}
          onClose={() => (deleting ? undefined : setDeleteTarget(null))}
          maxWidth="xs"
          fullWidth
        >
          <DialogTitle>Delete task?</DialogTitle>
          <DialogContent sx={{ pt: '8px !important' }}>
            <Typography variant="body2">
              {deleteTarget?.calendar_event_id
                ? 'This removes the task and its linked calendar event. The attached note stays in Drive.'
                : 'This removes the task from the board. The attached note stays in Drive.'}
            </Typography>
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setDeleteTarget(null)} disabled={deleting}>
              Cancel
            </Button>
            <Button color="error" variant="contained" onClick={() => void handleDeleteTask()} disabled={deleting}>
              {deleting ? 'Deleting…' : 'Delete'}
            </Button>
          </DialogActions>
        </Dialog>
      </Stack>
    </PageLayout>
  );
};

export default TasksPage;
